/**
 * Persona Engine TypeScript Type Definitions
 *
 * Shared types for the Persona Engine used across
 * portal, consumer, and extension frontends.
 */

// =============================================================================
// ENUMS
// =============================================================================

export type EngineType = 'ipc' | 'cat' | 'rft' | 'aic' | 'mi' | 'scarf'

export type ModifierType = 'politeness_theory' | 'cat_damping' | 'scarf_threat_check'

export type ModifierTier = 'safety' | 'style'

export type ArchetypeCategory = 'companion' | 'assistant' | 'engage_stock'

export type InjectionProfile = 'core' | 'full'

export type TransparencyMode = 'hidden' | 'configurable' | 'compliance'

export type FailoverStepName =
  | 'selected_engine'
  | 'auto_selected'
  | 'cat_fallback'
  | 'floor_only'
  | 'cold_start'

// =============================================================================
// ENGINE INFO
// =============================================================================

export interface PersonaEngineInfo {
  engine_name: EngineType
  algorithm_version: string
  description: string
  default_threshold: number
  reads_cognitive: boolean
  reads_decision: boolean
}

// =============================================================================
// ENGINE RESULT
// =============================================================================

export interface EngineResult {
  engine_name: EngineType
  algorithm_version: string
  confidence_score: number
  persona_dimensions: Record<string, number | Record<string, unknown>>
  adaptation_triggers: string[]
  avoidance_rules: string[]
  namespace_declarations: string[]
  raw_computation: Record<string, unknown>
}

// =============================================================================
// MODIFIER RESULT
// =============================================================================

export interface ModifierResult {
  modifier_name: ModifierType
  tier: ModifierTier
  applied: boolean
  adjustments: Record<string, {
    old: number
    new: number
    reason: string
  }>
  conflicts: string[]
}

// =============================================================================
// FAILOVER
// =============================================================================

export interface FailoverStep {
  step: FailoverStepName
  engine_attempted: EngineType | null
  threshold_required: number | null
  confidence_available: number | null
  outcome: string
}

// =============================================================================
// FRAMEWORK CONFIG
// =============================================================================

export interface FrameworkConfig {
  primaryEngine: EngineType
  modifiers: ModifierType[]
  modifierOrder: ModifierType[]
  autoSelected: boolean
  productLens: string
  overrideProductDefault: boolean
  archetypeId: string | null
  archetypeCategory: ArchetypeCategory | null
  engineActivationThreshold: number
  stateLayerReversionMessages: number
  stateLayerReversionMinutes: number
  injectionProfile: InjectionProfile
}

// =============================================================================
// ARCHETYPE
// =============================================================================

export interface PersonaEngineArchetype {
  archetype_id: string
  display_name: string
  description: string
  category: ArchetypeCategory
  default_engine: EngineType
  default_modifiers: ModifierType[]
  min_confidence_threshold: number
  domain_directives: Record<string, unknown>
  guardrail_profile: Record<string, unknown>
  warmth_floor_override: number | null
  is_active: boolean
  base_persona_object: Record<string, number>
  authored_narrative: string | null
  platform_variants: Record<string, string>
  product_context: string
  version: number
}

// =============================================================================
// CONFIG
// =============================================================================

export interface PersonaEngineConfig {
  id: number
  tenant_id: string
  engine_selection: string
  modifier_config: {
    modifiers: ModifierType[]
    order?: ModifierType[]
  }
  confidence_thresholds: Record<EngineType, number>
  state_layer_reversion_messages: number
  state_layer_reversion_minutes: number
  injection_profile: InjectionProfile
  transparency_mode: TransparencyMode
  is_active: boolean
  version: number
}

export interface PersonaEngineConfigUpdate {
  engine_selection?: string
  modifier_config?: Record<string, unknown>
  confidence_thresholds?: Record<string, number>
  state_layer_reversion_messages?: number
  state_layer_reversion_minutes?: number
  injection_profile?: string
  transparency_mode?: string
  is_active?: boolean
}

// =============================================================================
// REQUEST / RESPONSE
// =============================================================================

export interface PersonaEngineRenderRequest {
  profile_id: number
  product_context: string
  tenant_id?: string
  archetype_id?: string
  engine_override?: EngineType
  dry_run?: boolean
}

export interface PersonaEngineRenderResponse {
  engine_result: EngineResult
  modifier_results: ModifierResult[]
  failover_chain: FailoverStep[]
  final_dimensions: Record<string, number>
  framework_config: FrameworkConfig
  transparency: Record<string, unknown>
}

// =============================================================================
// GENERATION LOG
// =============================================================================

export interface PersonaEngineLogEntry {
  id: number
  persona_id: string
  profile_id: number
  engine_name: EngineType
  engine_algorithm_version: string
  engine_confidence_score: number
  failover_chain: FailoverStep[]
  modifier_log: ModifierResult[]
  archetype_id: string | null
  product_context: string
  generation_timestamp: string
}

export interface PersonaEngineLogResponse {
  entries: PersonaEngineLogEntry[]
  total: number
  limit: number
  offset: number
}

// =============================================================================
// HEALTH
// =============================================================================

export interface PersonaEngineHealthResponse {
  status: string
  version: string
  engines_available: EngineType[]
  archetype_count: number
  feature_enabled: boolean
  graduation_enabled: boolean
  instances_enabled: boolean
  roles_enabled: boolean
  role_template_count: number
  archetype_counts_by_context: Record<string, number>
}

// =============================================================================
// v3.0 TYPES
// =============================================================================

export type GraduationStage = 'base' | 'emerging' | 'established' | 'mature'

export interface PersonaEngineInstance {
  persona_id: string
  instance_name: string | null
  archetype_id: string | null
  archetype_category: string | null
  graduation_stage: GraduationStage
  graduation_dimensions_derived: number
  communication_briefing: Record<string, number>
  product_context: string
  is_current: boolean
  archetype_version: number | null
  created_at: string | null
}

export interface InstanceCreateRequest {
  archetype_id: string
  instance_name: string
}

export interface InstanceRenameRequest {
  instance_name: string
}

export interface DimensionGraduationDetail {
  dimension: string
  archetype_default: number | null
  current_value: number | null
  is_profile_derived: boolean
  source: 'archetype' | 'profile'
}

export interface GraduationStatus {
  stage: GraduationStage
  dimensions_derived: number
  total_dimensions: number
  dimension_details: DimensionGraduationDetail[]
  persona_id: string | null
  instance_name: string | null
  archetype_id: string | null
}

export interface RoleTemplate {
  role_template_id: string
  display_name: string
  description: string
  product_context: string
  domain_directives: string[]
  avoidance_rules: string[]
  dimension_nudges: Record<string, number>
  guardrail_extensions: Record<string, unknown>
  is_active: boolean
  tenant_id: string | null
  version: number
}

export interface RoleTemplateCreate {
  role_template_id: string
  display_name: string
  description: string
  product_context?: string
  domain_directives?: string[]
  avoidance_rules?: string[]
  dimension_nudges?: Record<string, number>
  guardrail_extensions?: Record<string, unknown>
}

export interface RoleTemplateUpdate {
  display_name?: string
  description?: string
  domain_directives?: string[]
  avoidance_rules?: string[]
  dimension_nudges?: Record<string, number>
  guardrail_extensions?: Record<string, unknown>
}

// =============================================================================
// ENGINE DISPLAY METADATA
// =============================================================================

export const ENGINE_LABELS: Record<EngineType, string> = {
  ipc: 'IPC — Interpersonal Circumplex',
  cat: 'CAT — Communication Accommodation',
  rft: 'RFT — Regulatory Focus',
  aic: 'AIC — Attachment-Informed',
  mi: 'MI — Motivational Interviewing',
  scarf: 'SCARF — Status/Certainty/Autonomy/Relatedness/Fairness',
}

export const ENGINE_SHORT_LABELS: Record<EngineType, string> = {
  ipc: 'IPC',
  cat: 'CAT',
  rft: 'RFT',
  aic: 'AIC',
  mi: 'MI',
  scarf: 'SCARF',
}

export const MODIFIER_LABELS: Record<ModifierType, string> = {
  politeness_theory: 'Politeness Theory',
  cat_damping: 'CAT Damping',
  scarf_threat_check: 'SCARF Threat Check',
}

export const MODIFIER_TIER_LABELS: Record<ModifierTier, string> = {
  safety: 'Safety',
  style: 'Style',
}

export const ALL_ENGINES: EngineType[] = ['ipc', 'cat', 'rft', 'aic', 'mi', 'scarf']

export const ALL_MODIFIERS: ModifierType[] = ['politeness_theory', 'cat_damping', 'scarf_threat_check']
